import { useEffect, useRef, useState } from "react";

// Components
import {
  shorthands,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  Spinner,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useNotify } from "../../../shared/components/SuperToast";
import CiTypeImage from "./CiTypeImage";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import ImagePicker from "../../../shared/components/ImagePicker";

// Styles
import "../../../styles/animations.css";

// Business logic
import { useSelectedCiTypeContext } from "../context/selectedCiTypeContext";
import { useCiTypeQuery } from "../hooks/useCiTypeQueries";
import { useCreateCiType, useUpdateCiType, useUpdateCiTypeIcon } from "../hooks/useCiTypeMutations";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import { ciTypeSchema } from "@shared/schemas";
import CiTypesTree from "./CiTypesTree";

// Variables
//const apiUrl = process.env.REACT_APP_API_URL?.trim();

const CiTypeDrawer = ({ isOpen, setIsOpen, newCiType }) => {
  const { t } = useTranslation();
  const { value: selectedCiType } = useSelectedCiTypeContext();
  const [currentCiType, setCurrentCiType] = useState(null);
  const { notify } = useNotify();
  const publicId = selectedCiType?.public_id;

  // Validation
  const validateCiType = useItemValidation(ciTypeSchema.$defs.citype);
  const [validationErrors, setValidationErrors] = useState({});

  // Errors
  const [errors, setErrors] = useState([]);

  // Image
  const [pendingFile, setPendingFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const fileInputRef = useRef();

  // 1. Query and mutations
  const { data, error, isError, isLoading, isSuccess } = useCiTypeQuery(publicId, isOpen && !newCiType);
  const {
    mutate: createCiType,
    isPending: isCreating,
    error: errorCreating,
  } = useCreateCiType(
    "citypes",
    (data) => {
      if (pendingFile)
        updateCiTypeIcon({ item: data, file: pendingFile }); // Pass to the icon update function if success
      else {
        notify(t("item.messages.created", { name: data.name }), "success");
        handleClose();
      }
    },
    null,
    true,
  );
  const {
    mutate: updateCiType,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateCiType(
    "citypes",
    (data) => {
      if (pendingFile) updateCiTypeIcon({ item: data, file: pendingFile });
      else {
        notify(t("item.messages.updated", { name: data.name }), "success");
        handleClose();
      }
    },
    null,
    true,
  );
  const {
    mutate: updateCiTypeIcon,
    isPending: isUpdatingIcon,
    error: errorUpdatingIcon,
    reset: resetUpdatingIcon,
  } = useUpdateCiTypeIcon(
    "citypes",
    (data) => {
      notify(t(newCiType ? "item.messages.created" : "item.messages.updated", { name: data.name }), "success");
      handleClose();
    },
    (err) => {
      notify(
        t(newCiType ? "item.messages.createdButIcon" : "item.messages.updatedButIcon", { name: currentCiType.name }),
        "warning",
      );
      handleClose();
    },
  );
  // 2. Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (errorCreating) {
      newErrors.push({ message: errorCreating?.message || t("generic.error"), type: "error", details: errorCreating });
    }
    if (errorUpdating) {
      newErrors.push({ message: errorUpdating?.message || t("generic.error"), type: "error", details: errorUpdating });
    }
    if (errorUpdatingIcon) {
      newErrors.push({
        message: errorUpdatingIcon?.message || t("generic.error"),
        type: "error",
        details: errorUpdatingIcon,
      });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, errorCreating, errorUpdating, errorUpdatingIcon, t]);
  // 2. Update current CI type when data is fetched or when creating new CI type
  useEffect(() => {
    if (!isOpen) return;
    if (newCiType) {
      const initialCiType = { parent_id: selectedCiType?.id, name: "" };
      setCurrentCiType(initialCiType);
      setImageSrc(null);
      setValidationErrors(validateCiType(initialCiType));
    } else if (isSuccess && data) {
      setCurrentCiType(data);
      //const url = data.icon_url ? `${apiUrl}/citypes/${data.id}/icon` : null;
      setImageSrc(null);
    }
  }, [isOpen, newCiType, isSuccess, data]);

  useEffect(() => {
    if (isOpen && isError) {
      notify(error.message, "error");
      handleClose();
    }
  }, [isOpen, isError]);

  // 3. Handlers
  const handleClose = () => {
    setCurrentCiType(null);
    resetUpdating();
    resetUpdatingIcon();
    setValidationErrors({});
    setErrors([]);
    setPendingFile(null);
    setImageSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
    setIsOpen(false);
  };

  const handleFileChange = (file) => {
    if (file) {
      setPendingFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenChange = (event, data) => {
    console.log("handleOpenChange", data);
    if (data.type === "backdropClick") return; // Prevent closing on backdrop click
  };

  const updateField = (field, value) => {
    const updated = { ...currentCiType, [field]: value };
    setCurrentCiType(updated);
    setValidationErrors(validateCiType(updated));
  };

  // JSX component
  return (
    <div>
      <Drawer open={isOpen} onOpenChange={handleOpenChange} type="overlay" position="end" separator size="medium">
        <DrawerHeader>
          <DrawerHeaderTitle
            action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={handleClose} />}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              {(imageSrc || currentCiType?.icon_id) && (
                <CiTypeImage ciType={currentCiType} url={imageSrc} width={48} height={48} />
              )}
              <div style={{ display: "flex", flexDirection: "column" }}>
                {!currentCiType?.name ? t("citype.new") : currentCiType.name}
                <ImagePicker
                  label={t("generic.iconChange")}
                  onFileSelected={handleFileChange}
                  fileInputRef={fileInputRef}
                />
              </div>
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!newCiType && isLoading && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}

        <DrawerBody>
          <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
          <div>
            <Field
              label={t("generic.name")}
              value={currentCiType?.name}
              validationState={validationErrors.name ? "error" : undefined}
              required
            >
              <Input
                id="name"
                maxLength="50"
                value={currentCiType?.name ?? ""}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </Field>
          </div>
          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <DataValidationMessageBar validationErrors={validationErrors} />
          )}
          <div style={{ height: "1rem" }} />
          {t("generic.parent")}:
          <CiTypesTree
            showRootItem={true}
            hiddenItemId={currentCiType?.id}
            selectedCiType={currentCiType?.parent_id != null ? { id: currentCiType.parent_id } : null}
            setSelectedCiType={(ciType) => updateField("parent_id", ciType?.id ?? null)}
          />
        </DrawerBody>
        <DrawerFooter>
          <div style={{ display: "flex", justifyContent: "space-between", ...shorthands.gap("1rem") }}>
            <Button
              icon={isCreating || isUpdating || isUpdatingIcon ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={isCreating || isUpdating || isUpdatingIcon || Object.keys(validationErrors).length > 0}
              onClick={() =>
                newCiType ? createCiType(currentCiType, handleClose) : updateCiType(currentCiType, handleClose)
              }
            >
              {newCiType ? t("generic.create") : t("generic.update")}
            </Button>
            <Button onClick={handleClose}>{t("generic.cancel")}</Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  );
};

export default CiTypeDrawer;
